import React, { useState } from "react";
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Paper,
  Divider,
  Box,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { Download, FileSpreadsheet } from "lucide-react";
import { exportToExcel } from "../../store/modules/transactions/transactionActions";
import saveAs from "file-saver";

const IconWrapper = styled("span")(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: "inline-flex",
  alignItems: "center",
}));

interface ExportSectionProps {
  onExportComplete: () => void;
  onExportError: () => void;
}

const ExportSection: React.FC<ExportSectionProps> = ({
  onExportComplete,
  onExportError,
}) => {
  const theme = useTheme();
  const [isBusy, setIsBusy] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const handleExport = async () => {
    try {
      setIsBusy(true);
      const response = await exportToExcel();
      // Extract the Blob data from the FileResponse
      saveAs(
        response.data,
        `transactions_export_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      // setSnackbar({
      //   open: true,
      //   message: "Transactions exported to Excel successfully",
      //   severity: "success",
      // });
      onExportComplete();
    } catch (error) {
      console.error("Export error:", error);
      onExportError();
      // setSnackbar({
      //   open: true,
      //   message: "Failed to export transactions",
      //   severity: "error",
      // });
    } finally {
      setIsBusy(false);
    }
  };
  return (
    <Card
      elevation={0}
      sx={{ height: "100%", border: "1px solid", borderColor: "divider" }}
    >
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconWrapper>
              <Download size={20} color={theme.palette.primary.main} />
            </IconWrapper>
            <Typography variant="h6">Export Transactions</Typography>
          </Box>
        }
        subheader="Export transaction data to Excel spreadsheets"
        sx={{ pb: 1 }}
      />
      <Divider />
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Export all transaction records to Excel format for record keeping or
            analysis.
          </Typography>
        </Box>

        <Paper
          sx={{
            bgcolor: "grey.50",
            p: 3,
            textAlign: "center",
            mb: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "180px",
          }}
        >
          <FileSpreadsheet
            size={48}
            style={{ marginBottom: 16, color: theme.palette.primary.light }}
          />
          {/* <Typography variant="body2" sx={{ mb: 1 }}>
            Ready to export {transactionCount} transactions
          </Typography> */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 3, display: "block" }}
          >
            Files are exported in .xlsx format
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={isBusy}
            aria-busy={isBusy}
            startIcon={
              isBusy ? <CircularProgress size={16} /> : <Download size={16} />
            }
            sx={{
              minWidth: isMobile ? "100%" : "200px",
              mt: 2,
            }}
          >
            Export to Excel
          </Button>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Export Options
        </Typography>

        <Box>
          <FormControlLabel
            control={<Checkbox defaultChecked />}
            label={
              <Typography variant="body2">
                Include client contact information
              </Typography>
            }
          />

          <FormControlLabel
            control={<Checkbox defaultChecked />}
            label={
              <Typography variant="body2">Include transaction notes</Typography>
            }
          />

          <FormControlLabel
            control={<Checkbox defaultChecked />}
            label={
              <Typography variant="body2">Include commission data</Typography>
            }
          />
        </Box> */}
      </CardContent>
    </Card>
  );
};

export default ExportSection;
